// Utility Functions for Models

/**
 * General function to normalize a score to a 0-1 scale
 * @param {number} score - Original score
 * @param {number} min - Minimum value (maps to 0)
 * @param {number} max - Maximum value (maps to 1)
 * @returns {number} Normalized value
 */
export const normalizeScore = (score, min, max) => {
    if (score <= min) return 0;
    if (score >= max) return 1;
    return (score - min) / (max - min);
  };
  
  /**
   * Normalize Altman Z-score to 0-1 scale
   * @param {number} score - Altman Z-score
   * @returns {number} Normalized value
   */
  export const normalizeAltmanScore = (score) => normalizeScore(score, 1.0, 3.0);
  
  /**
   * Normalize Fulmer H-score to 0-1 scale
   * @param {number} score - Fulmer H-score
   * @returns {number} Normalized value
   */
  export const normalizeFulmerScore = (score) => normalizeScore(score, -1.0, 1.0);
  
  /**
   * Normalize Springate Z-score to 0-1 scale
   * @param {number} score - Springate Z-score
   * @returns {number} Normalized value
   */
  export const normalizeSpringateScore = (score) => normalizeScore(score, 0.5, 1.0);
  
  /**
   * Normalize Taffler Z-score to 0-1 scale
   * @param {number} score - Taffler Z-score
   * @returns {number} Normalized value
   */
  export const normalizeTafflerScore = (score) => normalizeScore(score, 0.1, 0.4);
  
  /**
   * Compute ratios for Altman model
   * @param {Object} financialData - Financial data object
   * @returns {Object} Ratios for Altman model
   */
  export const computeAltmanRatios = (financialData) => {
    const { totalAssets, totalLiabilities, workingCapital, retainedEarnings, ebit, equity, revenue } = financialData;
    if (totalAssets === 0 || totalLiabilities === 0) {
      throw new Error('Общие активы или обязательства не могут быть равны нулю');
    }
    return {
      x1: workingCapital / totalAssets,
      x2: retainedEarnings / totalAssets,
      x3: ebit / totalAssets,
      x4: equity / totalLiabilities,
      x5: revenue / totalAssets
    };
  };
  
  /**
   * Compute ratios for Fulmer model
   * @param {Object} financialData - Financial data object
   * @returns {Object} Ratios for Fulmer model
   */
  export const computeFulmerRatios = (financialData) => {
    const { totalAssets, totalLiabilities, currentLiabilities, retainedEarnings, ebit, revenue, equity, workingCapital } = financialData;
    if (totalAssets === 0 || totalLiabilities === 0 || equity === 0) {
      throw new Error('Общие активы, обязательства или капитал не могут быть равны нулю');
    }
    return {
      v1: retainedEarnings / totalAssets,
      v2: revenue / totalAssets,
      v3: ebit / equity,
      v4: workingCapital / totalLiabilities,
      v5: totalLiabilities / totalAssets,
      v6: currentLiabilities / totalAssets,
      v7: Math.log10(totalAssets),
      v8: workingCapital / totalLiabilities,
      v9: Math.log10(ebit / (ebit < 0 ? 0 : 1))
    };
  };
  
  /**
   * Compute ratios for Springate model
   * @param {Object} financialData - Financial data object
   * @returns {Object} Ratios for Springate model
   */
  export const computeSpringateRatios = (financialData) => {
    const { totalAssets, currentLiabilities, workingCapital, ebit, revenue } = financialData;
    if (totalAssets === 0 || currentLiabilities === 0) {
      throw new Error('Общие активы или краткосрочные обязательства не могут быть равны нулю');
    }
    return {
      a: workingCapital / totalAssets,
      b: ebit / totalAssets,
      c: ebit / currentLiabilities,
      d: revenue / totalAssets
    };
  };
  
  /**
   * Compute ratios for Taffler model
   * @param {Object} financialData - Financial data object
   * @returns {Object} Ratios for Taffler model
   */
  export const computeTafflerRatios = (financialData) => {
    const { totalAssets, totalLiabilities, currentAssets, currentLiabilities, ebit, revenue } = financialData;
    if (totalAssets === 0 || totalLiabilities === 0 || currentLiabilities === 0) {
      throw new Error('Общие активы, обязательства или краткосрочные обязательства не могут быть равны нулю');
    }
    return {
      x1: ebit / currentLiabilities,
      x2: currentAssets / totalLiabilities,
      x3: currentLiabilities / totalAssets,
      x4: revenue / totalAssets
    };
  };
  
  // Model Calculation Functions
  
  /**
   * Calculate Altman Z-score
   * @param {Object} financialData - Financial data object
   * @param {Object} companyProfile - Company profile object
   * @returns {Object} Result with score, risk level, and recommendations
   */
  export const calculateAltmanZScore = (financialData, companyProfile) => {
    const { totalAssets, totalLiabilities, workingCapital, retainedEarnings, ebit, equity, revenue } = financialData;
    if (totalAssets === 0 || totalLiabilities === 0) {
      throw new Error('Общие активы или обязательства не могут быть равны нулю');
    }
  
    const x1 = workingCapital / totalAssets;
    const x2 = retainedEarnings / totalAssets;
    const x3 = ebit / totalAssets;
    const x4 = equity / totalLiabilities;
    const x5 = revenue / totalAssets;
  
    const isPublic = companyProfile?.companyType === 'jsc';
    let zScore, thresholds, interpretation;
  
    if (isPublic) {
      zScore = 1.2 * x1 + 1.4 * x2 + 3.3 * x3 + 0.6 * x4 + 0.999 * x5;
      thresholds = { safe: 2.99, gray: 1.81, distress: 1.81 };
    } else {
      zScore = 0.717 * x1 + 0.847 * x2 + 3.107 * x3 + 0.42 * x4 + 0.998 * x5;
      thresholds = { safe: 2.9, gray: 1.23, distress: 1.23 };
    }
  
    if (zScore > thresholds.safe) {
      interpretation = 'Финансово устойчивое состояние, низкая вероятность банкротства.';
    } else if (zScore > thresholds.gray) {
      interpretation = 'Неопределенное финансовое положение, умеренный риск банкротства.';
    } else {
      interpretation = 'Высокая вероятность банкротства в ближайшие 1-2 года.';
    }
  
    const riskLevel = zScore > thresholds.safe ? 'Низкий' :
                      zScore > thresholds.gray ? 'Умеренный' :
                      zScore > thresholds.distress * 0.7 ? 'Высокий' : 'Очень высокий';
  
    const recommendations = [];
    if (x1 < 0.1) recommendations.push('Увеличьте рабочий капитал через повышение ликвидности активов или реструктуризацию обязательств.');
    if (x2 < 0.1) recommendations.push('Повысьте рентабельность деятельности и реинвестируйте прибыль.');
    if (x3 < 0.05) recommendations.push('Оптимизируйте операционную деятельность для увеличения EBIT.');
    if (x4 < 0.5) recommendations.push('Укрепите капитал, снизив долговую нагрузку.');
    if (x5 < 0.8) recommendations.push('Повысьте эффективность использования активов.');
  
    return {
      score: zScore,
      riskLevel,
      description: `Z-счет Альтмана: ${zScore.toFixed(2)}`,
      interpretation,
      recommendations,
      thresholds,
      ratios: { x1, x2, x3, x4, x5 }
    };
  };
  
  /**
   * Calculate Fulmer H-score
   * @param {Object} financialData - Financial data object
   * @param {Object} companyProfile - Company profile object
   * @returns {Object} Result with score, risk level, and recommendations
   */
  export const calculateFulmerModel = (financialData, companyProfile) => {
    const { totalAssets, totalLiabilities, currentLiabilities, retainedEarnings, ebit, revenue, equity, workingCapital, companyAge } = financialData;
    if (totalAssets === 0 || totalLiabilities === 0 || equity === 0) {
      throw new Error('Общие активы, обязательства или капитал не могут быть равны нулю');
    }
  
    const { v1, v2, v3, v4, v5, v6, v7, v8, v9 } = computeFulmerRatios(financialData);
    const hScore = 5.528 * v1 + 0.212 * v2 + 0.073 * v3 + 1.270 * v4 - 0.120 * v5 + 2.335 * v6 + 0.575 * v7 + 1.083 * v8 + 0.894 * v9 - 6.075;
  
    let riskLevel, interpretation;
    if (hScore > 0) {
      riskLevel = 'Низкий';
      interpretation = 'Компания финансово устойчива, риск банкротства низкий.';
    } else if (hScore > -0.5) {
      riskLevel = 'Умеренный';
      interpretation = 'Неопределенное финансовое положение, требуется дополнительный анализ.';
    } else if (hScore > -1) {
      riskLevel = 'Высокий';
      interpretation = 'Высокая вероятность банкротства в ближайшем будущем.';
    } else {
      riskLevel = 'Критический';
      interpretation = 'Критическое финансовое положение, очень высокий риск банкротства.';
    }
  
    const recommendations = [];
    if (v1 < 0.1) recommendations.push('Увеличьте нераспределенную прибыль.');
    if (v2 < 1.0) recommendations.push('Повысьте эффективность активов.');
    if (v3 < 0.1) recommendations.push('Улучшите операционную эффективность.');
    if (v4 < 0.2) recommendations.push('Оптимизируйте оборотные активы.');
    if (v5 > 0.7) recommendations.push('Снизьте долговую нагрузку.');
  
    return {
      score: hScore,
      riskLevel,
      description: `H-показатель Фулмера: ${hScore.toFixed(2)}`,
      interpretation,
      recommendations,
      thresholds: { safe: 0, gray: -0.5, distress: -1 },
      ratios: { v1, v2, v3, v4, v5, v6, v7, v8, v9 }
    };
  };
  
  /**
   * Calculate Springate Z-score
   * @param {Object} financialData - Financial data object
   * @returns {Object} Result with score, risk level, and recommendations
   */
  export const calculateSpringateModel = (financialData) => {
    const { totalAssets, currentLiabilities, workingCapital, ebit, revenue } = financialData;
    if (totalAssets === 0 || currentLiabilities === 0) {
      throw new Error('Общие активы или краткосрочные обязательства не могут быть равны нулю');
    }
  
    const { a, b, c, d } = computeSpringateRatios(financialData);
    const zScore = 1.03 * a + 3.07 * b + 0.66 * c + 0.4 * d;
  
    let riskLevel, interpretation;
    if (zScore > 0.862) {
      riskLevel = 'Низкий';
      interpretation = 'Компания финансово устойчива, низкая вероятность банкротства.';
    } else if (zScore > 0.7) {
      riskLevel = 'Умеренный';
      interpretation = 'Существует умеренный риск банкротства.';
    } else if (zScore > 0.5) {
      riskLevel = 'Высокий';
      interpretation = 'Высокий риск банкротства в ближайшем будущем.';
    } else {
      riskLevel = 'Критический';
      interpretation = 'Критическое финансовое положение.';
    }
  
    const recommendations = [];
    if (a < 0.1) recommendations.push('Увеличьте рабочий капитал.');
    if (b < 0.05) recommendations.push('Повысьте операционную эффективность.');
    if (c < 0.1) recommendations.push('Реструктуризируйте обязательства.');
    if (d < 0.7) recommendations.push('Увеличьте выручку.');
  
    return {
      score: zScore,
      riskLevel,
      description: `Z-показатель Спрингейта: ${zScore.toFixed(2)}`,
      interpretation,
      recommendations,
      thresholds: { safe: 0.862, gray: 0.7, distress: 0.5 },
      ratios: { a, b, c, d }
    };
  };
  
  /**
   * Calculate Taffler Z-score
   * @param {Object} financialData - Financial data object
   * @returns {Object} Result with score, risk level, and recommendations
   */
  export const calculateTafflerModel = (financialData) => {
    const { totalAssets, totalLiabilities, currentAssets, currentLiabilities, ebit, revenue } = financialData;
    if (totalAssets === 0 || totalLiabilities === 0 || currentLiabilities === 0) {
      throw new Error('Общие активы, обязательства или краткосрочные обязательства не могут быть равны нулю');
    }
  
    const { x1, x2, x3, x4 } = computeTafflerRatios(financialData);
    const zScore = 0.53 * x1 + 0.13 * x2 + 0.18 * x3 + 0.16 * x4;
  
    let riskLevel, interpretation;
    if (zScore > 0.3) {
      riskLevel = 'Низкий';
      interpretation = 'Компания имеет хорошие долгосрочные перспективы.';
    } else if (zScore > 0.2) {
      riskLevel = 'Умеренный';
      interpretation = 'Неопределенное финансовое положение.';
    } else if (zScore > 0.1) {
      riskLevel = 'Высокий';
      interpretation = 'Высокий риск банкротства.';
    } else {
      riskLevel = 'Критический';
      interpretation = 'Критическое финансовое положение.';
    }
  
    const recommendations = [];
    if (x1 < 0.2) recommendations.push('Улучшите операционную прибыль.');
    if (x2 < 0.5) recommendations.push('Увеличьте ликвидность активов.');
    if (x3 > 0.5) recommendations.push('Уменьшите краткосрочные обязательства.');
    if (x4 < 0.8) recommendations.push('Повысьте оборачиваемость активов.');
  
    return {
      score: zScore,
      riskLevel,
      description: `Z-показатель Таффлера: ${zScore.toFixed(2)}`,
      interpretation,
      recommendations,
      thresholds: { safe: 0.3, gray: 0.2, distress: 0.1 },
      ratios: { x1, x2, x3, x4 }
    };
  };
  
  /**
   * Calculate Beaver Model score
   * @param {Object} financialData - Financial data object
   * @returns {Object} Result with score, risk level, and recommendations
   */
  export const calculateBeaverModel = (financialData) => {
    const { totalAssets, totalLiabilities, currentAssets, currentLiabilities, ebit, equity } = financialData;
    if (totalAssets === 0 || totalLiabilities === 0 || currentLiabilities === 0) {
      throw new Error('Общие активы, обязательства или краткосрочные обязательства не могут быть равны нулю');
    }
  
    const netIncome = ebit * 0.8; // Approximation: net income after taxes
    const depreciation = totalAssets * 0.05; // Approximation: depreciation as 5% of assets
    const beaverRatio = (netIncome + depreciation) / totalLiabilities;
    const roa = netIncome / totalAssets;
    const debtRatio = totalLiabilities / totalAssets;
    const workingCapitalRatio = (currentAssets - currentLiabilities) / totalAssets;
    const currentRatio = currentAssets / currentLiabilities;
  
    let riskGroup, riskLevel, interpretation;
    if (beaverRatio > 0.4 && roa > 0.06 && debtRatio < 0.35 && workingCapitalRatio > 0.4 && currentRatio > 2) {
      riskGroup = 1;
      riskLevel = 'Низкий';
      interpretation = 'Благополучное финансовое состояние.';
    } else if (beaverRatio > 0.17 && roa > 0.04 && debtRatio < 0.5 && workingCapitalRatio > 0.3 && currentRatio > 1) {
      riskGroup = 2;
      riskLevel = 'Умеренный';
      interpretation = 'Есть признаки ухудшения состояния.';
    } else if (beaverRatio < 0.17 && roa < 0.04 && debtRatio > 0.5 && workingCapitalRatio < 0.3 && currentRatio < 1) {
      riskGroup = 3;
      riskLevel = 'Высокий';
      interpretation = 'Высокий риск банкротства.';
    } else {
      riskGroup = 2.5;
      riskLevel = 'Умеренно высокий';
      interpretation = 'Смешанные показатели.';
    }
  
    const score = (3 - riskGroup) / 2;
    const recommendations = [];
    if (beaverRatio < 0.2) recommendations.push('Увеличьте денежный поток.');
    if (roa < 0.04) recommendations.push('Повысьте рентабельность активов.');
    if (debtRatio > 0.5) recommendations.push('Снизьте долговую нагрузку.');
    if (workingCapitalRatio < 0.3) recommendations.push('Увеличьте оборотный капитал.');
    if (currentRatio < 2) recommendations.push('Повысьте текущую ликвидность.');
  
    return {
      score,
      riskLevel,
      description: `Группа риска по Биверу: ${riskGroup.toFixed(1)}`,
      interpretation,
      recommendations,
      thresholds: { safe: 0.7, gray: 0.5, distress: 0.3 },
      ratios: { beaverRatio, roa, debtRatio, workingCapitalRatio, currentRatio }
    };
  };
  
  /**
   * Calculate Saifullin-Kadykov Model score
   * @param {Object} financialData - Financial data object
   * @returns {Object} Result with score, risk level, and recommendations
   */
  export const calculateSaifullinKadykovModel = (financialData) => {
    const { totalAssets, totalLiabilities, currentAssets, currentLiabilities, retainedEarnings, ebit, revenue, equity } = financialData;
    if (totalAssets === 0 || currentLiabilities === 0 || equity === 0) {
      throw new Error('Общие активы, краткосрочные обязательства или капитал не могут быть равны нулю');
    }
  
    const k1 = equity / totalAssets;
    const k2 = currentAssets / currentLiabilities;
    const k3 = revenue / totalAssets;
    const k4 = ebit / revenue;
    const k5 = (ebit * 0.8) / equity; // Corrected: Approximation of ROE
  
    const rScore = 2 * k1 + 0.1 * k2 + 0.08 * k3 + 0.45 * k4 + k5;
    let riskLevel, interpretation;
    if (rScore > 1) {
      riskLevel = 'Низкий';
      interpretation = 'Финансовое состояние хорошее.';
    } else if (rScore > 0.5) {
      riskLevel = 'Умеренный';
      interpretation = 'Удовлетворительное состояние.';
    } else if (rScore > 0.2) {
      riskLevel = 'Высокий';
      interpretation = 'Неудовлетворительное состояние.';
    } else {
      riskLevel = 'Критический';
      interpretation = 'Критическое состояние.';
    }
  
    const recommendations = [];
    if (k1 < 0.1) recommendations.push('Увеличьте долю собственного капитала.');
    if (k2 < 2) recommendations.push('Повысьте текущую ликвидность.');
    if (k3 < 1) recommendations.push('Увеличьте оборачиваемость активов.');
    if (k4 < 0.05) recommendations.push('Повысьте рентабельность продаж.');
    if (k5 < 0.1) recommendations.push('Повысьте рентабельность капитала.');
  
    return {
      score: rScore,
      riskLevel,
      description: `R-показатель Сайфуллина-Кадыкова: ${rScore.toFixed(2)}`,
      interpretation,
      recommendations,
      thresholds: { safe: 1, gray: 0.5, distress: 0.2 },
      ratios: { k1, k2, k3, k4, k5 }
    };
  };